import React, { useRef, useMemo, useEffect, useState } from 'react';
import { useFrame } from '../../utils/useFrameP1'; // P1: igual ao do r3f; com ?dbg=1 mede o tempo dos handlers
import { Line } from '@react-three/drei';
import * as THREE from 'three';

interface ProgressiveContourProps {
  points: number[]; // Flat array of [x,y,z, x,y,z, x,y,z, x,y,z, x,y,z] for the 4 segments of a box
  isActive: boolean;
  isCompleted: boolean;
  color?: string;
  duration?: number;
  onComplete?: () => void;
}

export const ProgressiveContour: React.FC<ProgressiveContourProps> = ({ 
  points, 
  isActive, 
  isCompleted, 
  color = "#ffffff", 
  duration = 1000,
  onComplete
}) => {
  const lineRef = useRef<any>(null);
  const startTimeRef = useRef<number | null>(null);
  const hasCompletedRef = useRef(false);

  // Group flat points into Vector3

  const pointsString = points.join(',');

  const vecs = useMemo(() => {
    const v = [];
    for (let i = 0; i < points.length; i += 3) {
      v.push(new THREE.Vector3(points[i], points[i + 1], points[i + 2]));
    }
    return v;
  }, [points, pointsString]);

  // Compute cumulative lengths of the segments
  const { totalLength, segmentLengths } = useMemo(() => {
    let total = 0;
    const segs = [];
    for (let i = 0; i < vecs.length - 1; i++) {
      const len = vecs[i].distanceTo(vecs[i + 1]);
      segs.push({ len, cumulative: total + len, start: total, v1: vecs[i], v2: vecs[i + 1] });
      total += len;
    }
    return { totalLength: total, segmentLengths: segs };
  }, [vecs]);

  useEffect(() => {
    if (isCompleted) {
      if (lineRef.current && lineRef.current.geometry) {
         lineRef.current.geometry.setPositions(points);
         lineRef.current.visible = true;
      }
      startTimeRef.current = null;
      hasCompletedRef.current = true;
    } else if (isActive) {
      if (startTimeRef.current === null) {
        startTimeRef.current = performance.now();
      }
      hasCompletedRef.current = false;
    } else {
      if (!hasCompletedRef.current) {
        if (lineRef.current) lineRef.current.visible = false;
        startTimeRef.current = null;
        hasCompletedRef.current = false;
      }
    }
  }, [isActive, isCompleted, points, pointsString]);

  useFrame(() => {
    if (isActive && !isCompleted && startTimeRef.current !== null) {
      const elapsed = performance.now() - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1.0);
      
      const currentDist = progress * totalLength;
      
      const newPoints: number[] = [];
      newPoints.push(vecs[0].x, vecs[0].y, vecs[0].z);

      for (const seg of segmentLengths) {
        if (currentDist > seg.start) {
          if (currentDist >= seg.cumulative) {
            newPoints.push(seg.v2.x, seg.v2.y, seg.v2.z);
          } else {
            const segProgress = (currentDist - seg.start) / seg.len;
            const interp = new THREE.Vector3().lerpVectors(seg.v1, seg.v2, segProgress);
            newPoints.push(interp.x, interp.y, interp.z);
            break;
          }
        }
      }
      
      if (lineRef.current && lineRef.current.geometry) {
        if (newPoints.length >= 6) {
          lineRef.current.geometry.setPositions(newPoints);
          lineRef.current.visible = true;
        } else {
          lineRef.current.visible = false;
        }
      }
      
      if (progress >= 1.0 && !hasCompletedRef.current) {
        hasCompletedRef.current = true;
        if (onComplete) {
          onComplete();
        }
      }
    }
  });

  // Handle initial points for the Line component
  const initialPoints = useMemo(() => {
    if (isCompleted) {
       const arr = [];
       for(let i = 0; i < points.length; i+=3){
           arr.push(new THREE.Vector3(points[i], points[i+1], points[i+2]));
       }
       return arr;
    }
    return [new THREE.Vector3(vecs[0].x, vecs[0].y, vecs[0].z), new THREE.Vector3(vecs[0].x, vecs[0].y, vecs[0].z)];
  }, [isCompleted, points, vecs]);

  if (!isActive && !isCompleted) return null;

  return (
    <Line 
      ref={lineRef}
      points={initialPoints} 
      color={color} 
      lineWidth={2} // pixel width for Line component
      opacity={0.8} 
      transparent={true} 
      depthWrite={false} 
      renderOrder={10} 
    />
  );
};
